// Created: 2026-08-13
// Updated: 2026-08-13
//
// Browse/search query hooks (react-query-browse-search, Req 2, 3, 4, 5, 6).
//
// Thin layer, same shape as slices 1–3 (`queries/{material,pattern,craftProject}/queries.ts`): the
// transport stays in `src/api/**` and is NOT duplicated here, and the migrated surfaces consume these
// hooks instead of calling `fetch` themselves. EVERY hook forwards React Query's `signal` into its
// fetcher — that forward is LOAD-BEARING, not an unused parameter: it is what turns navigating away
// mid-search into a CANCELLATION rather than a query error, which is this slice's one intentional
// behavioral delta (design § Error Handling).
//
// This is the first slice with LIST reads, so three things are established here for slice 5 onward:
//
//   1. ACCUMULATION uses `useInfiniteQuery` with the cursor in `pageParam`. All pages of one infinite
//      query live under ONE cache entry (`{pages, pageParams}`), so the cursor must never enter the key.
//      A surface that re-fetches one FIXED page instead would use `useQuery` with the page IN the key —
//      that form is recorded in Req 3.2 and has zero implementations in this slice.
//   2. NORMALIZE BEFORE KEYING, and pass the normalized value to the fetcher too, so the key and the URL
//      describe the same request (see `keys.ts`).
//   3. `staleTime: BROWSE_LIST_STALE_TIME_MS` (30 s) on every browse LIST read — EXCEPT
//      `useBrandMaterialsQuery`, which keeps the 5-minute baseline. No `queryClient.ts` default changes.
//
// The DEBOUNCE is deliberately NOT in this module. `SearchBar` owns its 300 ms timer and passes the
// SETTLED term to `usePatternTypeaheadQuery`; keying on raw input would mint one entry and one request
// per keystroke (design § D3).

import type { BlendedSearchResult, MaterialFilters } from '@mrb91002/shared-types'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { fetchBrandMaterials } from '../../api/fetchers/brand'
import { discoverMaterials } from '../../api/fetchers/materials'
import { fetchNewestPatternsFull, searchPatternsWithFilters } from '../../api/fetchers/patterns'
import { searchByDifficulty, searchByTag, searchPatterns, searchProjects } from '../../api/fetchers/search'
import { searchCollections } from '../../api/secure/collections'
import { useAuth } from '../../context/AuthContext'

import { BROWSE_LIST_STALE_TIME_MS, browseKeys, normalizeMaterialFilters, normalizeTerm } from './keys'

type SortByValue = 'newest' | 'highest-rated' | 'most-reviewed'
type DateRangeValue = 'day' | 'week' | 'month' | 'year' | 'all'

/** Minimum term length both search surfaces already enforced before firing a request. */
const MIN_SEARCH_TERM_LENGTH = 2

/** Total items fetched so far across all loaded pages — the offset the next page starts at. */
function flatCount<T extends { items?: unknown[] }>(pages: T[]): number {
  return pages.reduce((total, page) => total + (page.items?.length ?? 0), 0)
}

/**
 * PUBLIC — Home's newest-patterns list. Key `['newestPatterns', { limit }]`.
 *
 * DynamoDB cursor pagination: `lastKey` is an OPAQUE cursor carried in `pageParam`. `getNextPageParam`
 * returns `undefined` when the last page has no `lastKey`, which is exactly today's
 * `setHasMore(!!nextKey)` (Req 3.5).
 *
 * `enabled` is the BRANCH SELECTOR, not a loading guard: Home has two mutually exclusive reads served by
 * two different backends, and exactly one is live for any `(sortBy, dateRange)` pair (Req 3.8).
 */
export function useNewestPatternsInfiniteQuery(limit: number, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: browseKeys.newestPatterns(limit),
    // `signal` forward — the abort-as-cancellation seam. Keep it.
    queryFn: ({ pageParam, signal }) => fetchNewestPatternsFull(limit, pageParam, { signal }),
    initialPageParam: null as Record<string, unknown> | string | null,
    getNextPageParam: (lastPage) => lastPage.lastKey ?? undefined,
    enabled,
    staleTime: BROWSE_LIST_STALE_TIME_MS,
  })
}

/**
 * PUBLIC — Home's sorted/date-filtered list. Key `['patternsFiltered', { sortBy, dateRange, limit }]`.
 *
 * OpenSearch offset pagination: `from` is the accumulated item count, carried in `pageParam`. End of list
 * is "we have as many items as `total` allows", which mirrors today's
 * `setHasMore(fromValue + newItems.length < result.total)` (Req 3.6).
 *
 * The accumulated count is computed from ALL pages rather than from `pageParam + page.items.length` so a
 * short page cannot desynchronise the offset from what has actually been rendered.
 */
export function usePatternsFilteredInfiniteQuery(
  sortBy: SortByValue,
  dateRange: DateRangeValue,
  limit: number,
  enabled: boolean,
) {
  return useInfiniteQuery({
    queryKey: browseKeys.patternsFiltered(sortBy, dateRange, limit),
    queryFn: ({ pageParam, signal }) => searchPatternsWithFilters(sortBy, dateRange, limit, pageParam, { signal }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = flatCount(allPages)
      return loaded < lastPage.total ? loaded : undefined
    },
    enabled,
    staleTime: BROWSE_LIST_STALE_TIME_MS,
  })
}

/**
 * PUBLIC — pattern search. Key `['patternSearch', { q, limit, filters }]`.
 *
 * Both inputs are normalized BEFORE keying, and the normalized values are what go to the fetcher, so the
 * key and the URL always describe the same request: `''`/`'  '`/`'*'` are one entry, and `{}`,
 * `{brand: ''}`, `{brand: '   '}`, `{brand: undefined}` and `{unknownKey: 'x'}` are one entry (Req 1.3–1.7).
 *
 * Offset pagination bounded by the response's own `hasMore`, matching today's `setHasMore(searchResponse.hasMore)`.
 * The offset is the flat item count, i.e. exactly the `offset` the pre-migration `loadMore` sent.
 */
export function usePatternSearchInfiniteQuery(
  term: string,
  limit: number,
  rawFilters: MaterialFilters | undefined,
  enabled: boolean,
) {
  const q = normalizeTerm(term)
  const filters = normalizeMaterialFilters(rawFilters)

  return useInfiniteQuery({
    queryKey: browseKeys.patternSearch(q, limit, filters),
    queryFn: ({ pageParam, signal }) => searchPatterns(q, limit, pageParam, filters, { signal }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => (lastPage.hasMore ? flatCount(allPages) : undefined),
    enabled,
    staleTime: BROWSE_LIST_STALE_TIME_MS,
  })
}

/**
 * PUBLIC — craft-project search. Key `['projectSearch', { q, limit }]`.
 *
 * The response has no `hasMore` flag, so it is derived as `(from + size) < total` — the same expression
 * `SearchResults` computed inline before the migration — and the items are mapped into the
 * `{ type: 'project', score, project }` shape the page renders. That mapping stays HERE rather than in the
 * component so the cached page is already in render shape, and it is byte-for-byte the pre-migration
 * `.then(r => …)` mapping (Req 3.6, 7.2).
 */
export function useProjectSearchInfiniteQuery(term: string, limit: number, enabled: boolean) {
  const q = normalizeTerm(term)

  return useInfiniteQuery({
    queryKey: browseKeys.projectSearch(q, limit),
    queryFn: async ({ pageParam, signal }) => {
      const r = await searchProjects(q, limit, pageParam, { signal })
      return {
        items: r.items.map(
          (item) =>
            ({ type: 'project' as const, score: item._score, project: item }) as unknown as BlendedSearchResult,
        ),
        total: r.total,
        hasMore: r.from + r.size < r.total,
      }
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => (lastPage.hasMore ? flatCount(allPages) : undefined),
    enabled,
    staleTime: BROWSE_LIST_STALE_TIME_MS,
  })
}

/**
 * PUBLIC — patterns carrying one tag. Key `['tagSearch', { tag, limit }]`.
 *
 * A tag search is its own resource: the transport sends `?tag=`, not `?q=`, so the term normalizer does
 * not apply and the tag is keyed verbatim.
 */
export function useTagSearchInfiniteQuery(tag: string, limit: number, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: browseKeys.tagSearch(tag, limit),
    queryFn: ({ pageParam, signal }) => searchByTag(tag, limit, pageParam, { signal }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => (lastPage.hasMore ? flatCount(allPages) : undefined),
    enabled,
    staleTime: BROWSE_LIST_STALE_TIME_MS,
  })
}

/** PUBLIC — patterns at one difficulty. Key `['difficultySearch', { difficulty, limit }]`. Same shape as `useTagSearchInfiniteQuery`. */
export function useDifficultySearchInfiniteQuery(difficulty: string, limit: number, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: browseKeys.difficultySearch(difficulty, limit),
    queryFn: ({ pageParam, signal }) => searchByDifficulty(difficulty, limit, pageParam, { signal }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => (lastPage.hasMore ? flatCount(allPages) : undefined),
    enabled,
    staleTime: BROWSE_LIST_STALE_TIME_MS,
  })
}

/**
 * PUBLIC — the header typeahead. Key `['patternSearch', { q, limit: 8, filters: {} }]`.
 *
 * `settledTerm` MUST be the post-debounce value. The 300 ms timer stays in `SearchBar`, outside the query:
 * keying on raw input would create one cache entry and one request PER KEYSTROKE — nine for "amigurumi",
 * worse than today. Keying on the settled term preserves today's request count and adds a real win,
 * because backspacing to an already-searched term paints from cache with no request at all (design § D3).
 *
 * `useQuery`, not `useInfiniteQuery`: the dropdown has no second page. It shares the `patternSearch` key
 * FAMILY with `/search`, and `limit` (8 vs 50) is what keeps the two in separate entries so the 8-item
 * list can never be served into the 50-item page (Req 1.8).
 *
 * The `< 2` guard is today's, moved from an early `return` in the effect to `enabled`, so a one-character
 * term stays idle instead of fetching `q=a` (Req 2.4).
 */
export function usePatternTypeaheadQuery(settledTerm: string, limit = 8) {
  const q = normalizeTerm(settledTerm)
  const filters = normalizeMaterialFilters(undefined)

  return useQuery({
    queryKey: browseKeys.patternSearch(q, limit, filters),
    queryFn: ({ signal }) => searchPatterns(q, limit, 0, filters, { signal }),
    // Gated on the RAW settled term's length, not on `q`: `normalizeTerm('')` is `'*'`, which is 1
    // character but is not a term the typeahead should ever fetch.
    enabled: settledTerm.trim().length >= MIN_SEARCH_TERM_LENGTH,
    staleTime: BROWSE_LIST_STALE_TIME_MS,
  })
}

/**
 * PUBLIC — one brand's whole material catalogue. Key `['brandMaterials', brandName]`.
 *
 * `useQuery`, not `useInfiniteQuery`: the response IS the whole catalogue, there is no cursor.
 *
 * This is the ONE browse read with NO `staleTime` override — it keeps the QueryClient's 5-minute baseline
 * on purpose. A brand catalogue changes on the order of days, so the volatility argument that justifies
 * 30 s for search lists does not apply. Recording the contrast matters: the rule is TUNE TO VOLATILITY,
 * not always-override (Req 5.3).
 */
export function useBrandMaterialsQuery(brandName: string | undefined) {
  // Disabled without a brand, so this placeholder key is never fetched under.
  const brand = brandName ?? ''

  return useQuery({
    queryKey: browseKeys.brandMaterials(brand),
    queryFn: ({ signal }) => fetchBrandMaterials(brand, { signal }),
    enabled: !!brandName,
  })
}

/**
 * PUBLIC — collection search. Key `['collectionSearch', { q }]`.
 *
 * `useQuery`, not `useInfiniteQuery`, and that is deliberate. `CollectionSearchPage`'s `hasMore` is
 * initialised `false` and only ever assigned `false`, so its Load More button never renders and
 * `performSearch(query, collections.length)` is dead code. Wrapping an unreachable second page in
 * `useInfiniteQuery` would encode a pagination capability the surface does not have and would need a
 * `getNextPageParam: () => undefined` stub that asserts nothing. The dead path is a PRE-EXISTING DEFECT,
 * recorded and NOT fixed here — fixing it would change rendered behavior, which Req 7.6 forbids in this
 * slice (design § D1).
 *
 * The `{limit: '20', offset: '0'}` filter object is passed verbatim so the URL is byte-identical to
 * today's first-page request. `settledTerm` comes from `?q=`, which only changes on submit, so it is
 * already settled — no debounce needed.
 *
 * `normalizeTerm` deliberately does NOT apply here: this surface has no `query || '*'` substitution, and
 * it sends the term UNTRIMMED (only the emptiness guard trims, exactly as today). So the term is keyed
 * verbatim — `' cat '` and `'cat'` are two entries because they are two different requests (Req 7.1).
 */
export function useCollectionSearchQuery(settledTerm: string) {
  return useQuery({
    queryKey: browseKeys.collectionSearch(settledTerm),
    queryFn: ({ signal }) => searchCollections(settledTerm, { limit: '20', offset: '0' }, undefined, { signal }),
    // Today's `if (!searchQuery.trim()) { setCollections([]); return }` — an empty box fires no request.
    enabled: settledTerm.trim().length > 0,
    staleTime: BROWSE_LIST_STALE_TIME_MS,
  })
}

/**
 * PERSONAL — the discover feed. Key `['me', 'materialDiscovery', { category, limit }]`.
 *
 * The ONE identity-varying browse read in this slice, so it is the ONE `'me'`-keyed and token-gated one:
 * the handler filters `identity_id <> :userId`, so the feed excludes the caller's own materials and two
 * callers get two different lists from the same URL. `enabled: !!idToken` replaces today's
 * `if (!idToken) return` early return, so a signed-out visitor fires no request and the page renders its
 * "Please log in to discover materials" branch (Req 4.2, 7.5).
 *
 * Never dehydrated for SSG/ISR — unlike every other key in this module.
 */
export function useMyMaterialDiscoveryQuery(category: string | undefined, limit = 20) {
  const { idToken } = useAuth()

  return useQuery({
    queryKey: browseKeys.myMaterialDiscovery(category, limit),
    queryFn: ({ signal }) => discoverMaterials(limit, category, idToken ?? undefined, { signal }),
    enabled: !!idToken,
    staleTime: BROWSE_LIST_STALE_TIME_MS,
  })
}
